import type { BookingState, LoadSize, MaterialType, PricingResult, ServiceType, SkipSize, SupplementalItemId } from "@/types/booking";

export const serviceOptions: Array<{ id: ServiceType; name: string; description: string }> = [
  { id: "skip", name: "Skip hire", description: "Flexible skip sizes for household, trade, and renovation waste." },
  { id: "man-van", name: "Man and van", description: "A two-person collection service for bulky waste and light clearances." },
  { id: "grab", name: "Grab hire", description: "Fast muck-away collections for soil, hardcore, and inert material." },
];

export const skipSizes: Array<{ id: SkipSize; title: string; description: string; basePrice: number }> = [
  { id: "4-yard", title: "4 Yard Mini Skip", description: "Compact choice for small DIY jobs and garden clearances.", basePrice: 180 },
  { id: "6-yard", title: "6 Yard Midi Skip", description: "Popular domestic skip for kitchens, bathrooms, and bulky waste.", basePrice: 230 },
  { id: "8-yard", title: "8 Yard Builder Skip", description: "Trade-friendly option for heavier renovation loads.", basePrice: 285 },
  { id: "10-yard", title: "10 Yard Maxi Skip", description: "Extra capacity for larger household clearances.", basePrice: 340 },
  { id: "12-yard", title: "12 Yard Maxi Skip", description: "High-volume skip for light mixed waste and large projects.", basePrice: 390 },
];

export const loadSizes: Array<{ id: LoadSize; title: string; description: string; basePrice: number }> = [
  { id: "small", title: "Small load", description: "A few bulky items or a light room clearance.", basePrice: 95 },
  { id: "medium", title: "Medium load", description: "Several items, light refurbishment waste, or half-room clearances.", basePrice: 155 },
  { id: "large", title: "Large load", description: "Larger bulky collections and multi-room waste.", basePrice: 235 },
  { id: "full-van", title: "Full van load", description: "Maximum van capacity with a two-person team.", basePrice: 320 },
];

export const materialTypes: Array<{ id: MaterialType; title: string; description: string; basePrice: number }> = [
  { id: "soil", title: "Soil", description: "Clean soil and earth from landscaping or excavation.", basePrice: 260 },
  { id: "hardcore", title: "Hardcore", description: "Brick, rubble, concrete, and construction aggregate.", basePrice: 285 },
  { id: "mixed-inert", title: "Mixed inert waste", description: "Mixed inert loads suitable for grab collection.", basePrice: 320 },
  { id: "green-waste", title: "Green waste", description: "Branches, hedge cuttings, and garden material.", basePrice: 240 },
];

export const supplementalItems: Array<{ id: SupplementalItemId; title: string; price: number }> = [
  { id: "mattress", title: "Mattress", price: 25 },
  { id: "fridge-freezer", title: "Fridge/freezer", price: 45 },
  { id: "tyres", title: "Tyres", price: 12 },
  { id: "plasterboard", title: "Plasterboard", price: 55 },
  { id: "gas-bottle", title: "Gas bottle", price: 35 },
];

export function getPostcodeOutcode(postcode: string) {
  return postcode.trim().toUpperCase().split(/\s+/)[0] ?? "";
}

export function getPostcodeMultiplier(postcode: string) {
  const outcode = getPostcodeOutcode(postcode);
  if (["HP", "AL", "LU"].some((prefix) => outcode.startsWith(prefix))) return 1;
  if (["WD", "HA", "UB", "SL"].some((prefix) => outcode.startsWith(prefix))) return 1.08;
  if (["NW", "W", "N", "SW", "SE", "E"].some((prefix) => outcode.startsWith(prefix))) return 1.16;
  return 1.05;
}

export function calculatePricing(state: BookingState): PricingResult {
  const multiplier = getPostcodeMultiplier(state.postcode);
  const base = getBasePrice(state.service, state.skipSize, state.loadSize, state.materialType);
  const baseServicePrice = Math.round(base * multiplier);
  const lines = [{ label: "Base service", amount: baseServicePrice }];
  const supplementalItemsTotal = supplementalItems.reduce((total, item) => {
    const quantity = state.supplementalItems[item.id] ?? 0;
    if (quantity > 0) {
      lines.push({ label: `${item.title} x ${quantity}`, amount: item.price * quantity });
    }
    return total + item.price * quantity;
  }, 0);
  const subtotal = baseServicePrice + supplementalItemsTotal;
  const discount = Math.min(state.discountResult?.amount ?? 0, subtotal);
  const taxableTotal = Math.max(subtotal - discount, 0);
  const vat = Math.round(taxableTotal * 0.2);

  return {
    baseServicePrice,
    supplementalItemsTotal,
    discount,
    vat,
    totalPayable: taxableTotal + vat,
    lines,
  };
}

function getBasePrice(service: BookingState["service"], skipSize: BookingState["skipSize"], loadSize: BookingState["loadSize"], materialType: BookingState["materialType"]) {
  if (service === "skip") return skipSizes.find((item) => item.id === skipSize)?.basePrice ?? 0;
  if (service === "man-van") return loadSizes.find((item) => item.id === loadSize)?.basePrice ?? 0;
  if (service === "grab") return materialTypes.find((item) => item.id === materialType)?.basePrice ?? 0;
  return 0;
}
